import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import "moment/locale/es"; // Ensure Spanish locale
import { useSelector } from "react-redux";
import { IAuthSlice } from "../../interfaces/others/IAuthSlice";
import {
  createLeaderAvailability,
  getLeaderAvailabilityByUser,
  deleteLeaderAvailability,
  updateLeaderAvailability,
  getLeaderInterviews,
} from "../../services/leader-availabilities.service.ts";
import Layout from "../../components/Layout.tsx";
import { useAppSettings } from "../../hooks/useAppSettings.tsx";
import ModalContainer from "../../components/ModalContainer.tsx";

// Ensure moment is in Spanish
moment.locale("es");

const START_HOUR = 7;
const END_HOUR = 23;
const SLOTS_PER_HOUR = 2; // For 30 min intervals
const TOTAL_SLOTS = (END_HOUR - START_HOUR) * SLOTS_PER_HOUR;

const LeaderCalendar = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: { auth: IAuthSlice }) => state.auth);
  useAppSettings();

  const [currentDate, setCurrentDate] = useState(moment());
  const [viewMode, setViewMode] = useState<"week" | "day">("week");
  const [availabilities, setAvailabilities] = useState<any[]>([]);
  const [interviews, setInterviews] = useState<any[]>([]);

  // Selection State
  const [isDragging, setIsDragging] = useState(false);
  const [selectionStart, setSelectionStart] = useState<{
    dayIndex: number;
    slotIndex: number;
  } | null>(null);
  const [selectionEnd, setSelectionEnd] = useState<{
    dayIndex: number;
    slotIndex: number;
  } | null>(null);

  // Modal State
  const [showEditModal, setShowEditModal] = useState(false);
  const [showInterviewModal, setShowInterviewModal] = useState(false);
  const [showMyInterviewsModal, setShowMyInterviewsModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<any>(null);

  // Edit Form State
  const [editTitle, setEditTitle] = useState("");
  const [editStartTime, setEditStartTime] = useState("");
  const [editEndTime, setEditEndTime] = useState("");

  // Determine Days to Show
  const days = React.useMemo(() => {
    if (viewMode === "day") {
      return [currentDate.clone()];
    }
    // Week mode
    const weekStart = currentDate.clone().startOf("week");
    return Array.from({ length: 7 }, (_, i) =>
      weekStart.clone().add(i, "days")
    );
  }, [currentDate, viewMode]);

  const fetchData = async () => {
    if (!user?.id) return;
    try {
      const data = await getLeaderAvailabilityByUser(Number(user.id));
      setAvailabilities(data || []);

      const intData = await getLeaderInterviews(Number(user.id));
      setInterviews(intData || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [user, currentDate]);

  // --- Helpers ---
  const getSlotTime = (day: moment.Moment, slotIndex: number) => {
    const hour = START_HOUR + Math.floor(slotIndex / SLOTS_PER_HOUR);
    const minute = (slotIndex % SLOTS_PER_HOUR) * 30;
    return day.clone().hour(hour).minute(minute).second(0).milliseconds(0);
  };

  const getGridPosition = (dateStr: string) => {
    const date = moment(dateStr);
    const dayIndex = days.findIndex((d) => d.isSame(date, "day"));

    if (dayIndex === -1) return null;

    const hour = date.hour();
    const minute = date.minute();

    if (hour < START_HOUR || hour >= END_HOUR) return null;

    const slotIndex =
      (hour - START_HOUR) * SLOTS_PER_HOUR + (minute >= 30 ? 1 : 0);
    return { dayIndex, slotIndex };
  };

  // --- Handlers ---

  const handleMouseDown = (dayIndex: number, slotIndex: number) => {
    setIsDragging(true);
    setSelectionStart({ dayIndex, slotIndex });
    setSelectionEnd({ dayIndex, slotIndex });
  };

  const handleMouseEnter = (dayIndex: number, slotIndex: number) => {
    if (!isDragging || !selectionStart) return;
    if (dayIndex !== selectionStart.dayIndex) return;
    setSelectionEnd({ dayIndex, slotIndex });
  };

  const handleMouseUp = async () => {
    if (!isDragging || !selectionStart || !selectionEnd) {
      setIsDragging(false);
      setSelectionStart(null);
      setSelectionEnd(null);
      return;
    }

    const day = days[selectionStart.dayIndex];
    const startSlot = Math.min(
      selectionStart.slotIndex,
      selectionEnd.slotIndex
    );
    const endSlot = Math.max(selectionStart.slotIndex, selectionEnd.slotIndex);

    const startTime = getSlotTime(day, startSlot);
    const endTime = getSlotTime(day, endSlot).add(30, "minutes");

    setIsDragging(false);
    setSelectionStart(null);
    setSelectionEnd(null);

    try {
      await createLeaderAvailability({
        userId: Number(user.id),
        startTime: startTime.format(),
        endTime: endTime.format(),
        title: "Disponible",
      });
      await fetchData();
    } catch (error) {
      console.error("Error creating availability range", error);
      alert("Error al guardar disponibilidad");
    }
  };

  const handleEventClick = (e: React.MouseEvent, av: any) => {
    e.stopPropagation();
    setSelectedEvent(av);

    if (av.application) {
      // Is Interview
      setShowInterviewModal(true);
    } else {
      // Is Availability
      setEditTitle(av.title || "Disponible");
      setEditStartTime(moment(av.startTime).format("HH:mm"));
      setEditEndTime(moment(av.endTime).format("HH:mm"));
      setShowEditModal(true);
    }
  };

  const goToCandidateForm = (interview: any) => {
    navigate("/postulante/form", {
      state: {
        ...interview.application,
        isLeaderView: true,
      },
    });
  };

  const handleDelete = async () => {
    if (!selectedEvent) return;
    try {
      await deleteLeaderAvailability(selectedEvent.id);
      setShowEditModal(false);
      setSelectedEvent(null);
      await fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  const handleUpdate = async () => {
    if (!selectedEvent) return;
    const originalStart = moment(selectedEvent.startTime);

    const [newStartHour, newStartMinute] = editStartTime.split(":").map(Number);
    const [newEndHour, newEndMinute] = editEndTime.split(":").map(Number);

    const newStart = originalStart
      .clone()
      .hour(newStartHour)
      .minute(newStartMinute);
    const newEnd = originalStart.clone().hour(newEndHour).minute(newEndMinute);

    if (!newStart.isValid() || !newEnd.isValid()) {
      alert("Horas inválidas");
      return;
    }

    if (newEnd.isSameOrBefore(newStart)) {
      alert("La hora fin debe ser mayor a la hora inicio");
      return;
    }

    try {
      await updateLeaderAvailability(selectedEvent.id, {
        startTime: newStart.format(),
        endTime: newEnd.format(),
        title: editTitle,
      });
      setShowEditModal(false);
      setSelectedEvent(null);
      await fetchData();
    } catch (err) {
      console.error("Error updating", err);
      alert("Error al actualizar");
    }
  };

  // --- Render ---

  const availabilityBlocks = availabilities.map((av) => {
    const startPos = getGridPosition(av.startTime);
    if (!startPos) return null;

    const colStart = startPos.dayIndex + 2;
    const rowStart = startPos.slotIndex + 2;

    const startM = moment(av.startTime);
    const endM = moment(av.endTime);

    const durationSlots = Math.ceil(endM.diff(startM, "minutes") / 30);
    const rowEnd = rowStart + durationSlots;

    return (
      <div
        key={`avail-${av.id}`}
        style={{
          gridColumn: colStart,
          gridRowStart: rowStart,
          gridRowEnd: rowEnd,
          backgroundColor: "#cfe2ff",
          borderLeft: "4px solid #007bff",
          borderRadius: "4px",
          padding: "2px 4px",
          fontSize: "0.75rem",
          color: "#084298",
          overflow: "hidden",
          margin: "1px",
          position: "relative",
          cursor: "pointer",
          zIndex: 10,
          boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
        }}
        onClick={(e) => handleEventClick(e, av)}
        title={`${av.title || "Disponible"} ${moment(av.startTime).format(
          "HH:mm"
        )} - ${moment(av.endTime).format("HH:mm")}`}
      >
        <div
          style={{
            fontWeight: "bold",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {av.title || "Disponible"}
        </div>
        <div>
          {moment(av.startTime).format("HH:mm")} -{" "}
          {moment(av.endTime).format("HH:mm")}
        </div>
      </div>
    );
  });

  const interviewBlocks = interviews.map((int) => {
    const startPos = getGridPosition(int.startTime);
    if (!startPos) return null;

    const colStart = startPos.dayIndex + 2;

    // Logic for 15 minute slots
    const startM = moment(int.startTime);
    const minute = startM.minute();

    const hour = startM.hour();
    const baseSlotIndex =
      (hour - START_HOUR) * SLOTS_PER_HOUR + (minute >= 30 ? 1 : 0);
    const rowStart = baseSlotIndex + 2;

    const isOffset = minute === 15 || minute === 45;
    const marginTop = isOffset ? "20px" : "0px";
    const height = "20px";

    const isCompleted = int.application?.candidates?.[0]?.leaderNotes;
    const bgColor = isCompleted ? "#d4edda" : "#fff3cd";
    const borderColor = isCompleted ? "#28a745" : "#ffc107";
    const textColor = isCompleted ? "#155724" : "#856404";

    return (
      <div
        key={`int-${int.id}`}
        style={{
          gridColumn: colStart,
          gridRowStart: rowStart,
          gridRowEnd: rowStart + 1,
          marginTop: marginTop,
          height: height,
          backgroundColor: bgColor,
          borderLeft: `4px solid ${borderColor}`,
          borderRadius: "4px",
          padding: "0 4px",
          fontSize: "0.7rem",
          color: textColor,
          overflow: "hidden",
          marginLeft: "5px",
          marginRight: "1px",
          position: "relative",
          zIndex: 15,
          boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
        }}
        onClick={(e) => handleEventClick(e, int)}
        title={`Entrevista: ${
          int.application?.person?.firstName || "Candidato"
        } - ${moment(int.startTime).format("HH:mm")}`}
      >
        <div
          style={{
            fontWeight: "bold",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          <i className="fa-solid fa-comments"></i>:{" "}
          {int.application?.person?.firstName}{" "}
          {int.application?.person?.lastName}
        </div>
      </div>
    );
  });

  const eventBlocks = [...availabilityBlocks, ...interviewBlocks].filter(
    Boolean
  );

  let selectionBlock = null;
  if (isDragging && selectionStart && selectionEnd) {
    const colStart = selectionStart.dayIndex + 2;
    const startSlot = Math.min(
      selectionStart.slotIndex,
      selectionEnd.slotIndex
    );
    const endSlot = Math.max(selectionStart.slotIndex, selectionEnd.slotIndex);

    const rowStart = startSlot + 2;
    const rowEnd = endSlot + 1 + 2;
    // Preview Times
    const day = days[selectionStart.dayIndex];
    const previewStart = getSlotTime(day, startSlot);
    const previewEnd = getSlotTime(day, endSlot).add(30, "minutes");

    selectionBlock = (
      <div
        style={{
          gridColumn: colStart,
          gridRowStart: rowStart,
          gridRowEnd: rowEnd,
          backgroundColor: "rgba(40, 167, 69, 0.6)",
          border: "2px solid #28a745",
          borderRadius: "4px",
          pointerEvents: "none",
          zIndex: 20,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "white",
          fontSize: "0.8rem",
          fontWeight: "bold",
          flexDirection: "column",
        }}
      >
        <div>{previewStart.format("HH:mm")}</div>
        <div>-</div>
        <div>{previewEnd.format("HH:mm")}</div>
      </div>
    );
  }

  // Capitalize month
  const monthName = currentDate.format("MMMM");
  const capitalizedMonth =
    monthName.charAt(0).toUpperCase() + monthName.slice(1);
  const titleDate = `${capitalizedMonth} de ${currentDate.format("YYYY")}`;

  // Filter interviews for Today
  const myInterviewsToday = interviews.filter((int) =>
    moment(int.startTime).isSame(moment(), "day")
  );

  return (
    <Layout>
      <div
        className="p-4"
        style={{
          backgroundColor: "#fff",
          borderRadius: "8px",
          userSelect: "none",
        }}
        onMouseUp={handleMouseUp}
      >
        {/* Toolbar */}
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center mb-2 mb-md-0">
            <div className="btn-group me-4">
              <button
                className={`btn ${
                  viewMode === "week"
                    ? "btn-secondary"
                    : "btn-outline-secondary"
                }`}
                onClick={() => setViewMode("week")}
              >
                Semana
              </button>
              <button
                className={`btn ${
                  viewMode === "day" ? "btn-secondary" : "btn-outline-secondary"
                }`}
                onClick={() => setViewMode("day")}
              >
                Día
              </button>
            </div>
            <h3 className="mb-0 text-dark">{titleDate}</h3>
          </div>

          <div>
            <button
              className="btn btn-info text-white me-2"
              onClick={() => setShowMyInterviewsModal(true)}
            >
              <i className="fa fa-list-alt me-1"></i>
              Mis Entrevistas ({myInterviewsToday.length})
            </button>
            <button
              className="btn btn-outline-primary me-2"
              onClick={() => setCurrentDate(moment())}
            >
              Hoy
            </button>
            <button
              className="btn btn-icon btn-black me-1"
              onClick={() =>
                setCurrentDate(
                  currentDate
                    .clone()
                    .subtract(1, viewMode === "week" ? "week" : "day")
                )
              }
            >
              <i className="fa fa-chevron-left"></i>
            </button>
            <button
              className="btn btn-icon btn-black"
              onClick={() =>
                setCurrentDate(
                  currentDate
                    .clone()
                    .add(1, viewMode === "week" ? "week" : "day")
                )
              }
            >
              <i className="fa fa-chevron-right"></i>
            </button>
          </div>
        </div>

        {/* Scrollable Container */}
        <div
          style={{
            maxHeight: "calc(100vh - 200px)",
            overflowY: "auto",
            border: "1px solid #ddd",
            borderRadius: "8px",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: `60px repeat(${days.length}, 1fr)`,
              gridTemplateRows: `60px repeat(${TOTAL_SLOTS}, 40px)`,
              position: "relative",
            }}
          >
            {/* Header Row */}
            <div
              style={{
                gridRow: 1,
                gridColumn: 1,
                borderBottom: "1px solid #ddd",
                background: "#fff",
                zIndex: 40,
                position: "sticky",
                top: 0,
              }}
            ></div>
            {days.map((day, i) => {
              const isToday = day.isSame(moment(), "day");
              return (
                <div
                  key={i}
                  style={{
                    gridRow: 1,
                    gridColumn: i + 2,
                    borderBottom: "1px solid #ddd",
                    borderLeft: "1px solid #ddd",
                    background: "#fff",
                    zIndex: 40,
                    position: "sticky",
                    top: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "5px 0",
                  }}
                >
                  <span
                    style={{
                      fontSize: "0.8rem",
                      color: isToday ? "#1a73e8" : "#70757a",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      marginBottom: "4px",
                    }}
                  >
                    {day.format("ddd")}
                  </span>
                  <div
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "50%",
                      backgroundColor: isToday ? "#1a73e8" : "transparent",
                      color: isToday ? "#fff" : "#3c4043",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "1.2rem",
                      cursor: "pointer",
                      transition: "background-color 0.2s",
                    }}
                    onClick={() => {
                      if (viewMode === "week") {
                        setViewMode("day");
                        setCurrentDate(day);
                      }
                    }}
                  >
                    {day.format("D")}
                  </div>
                </div>
              );
            })}

            {/* Time Labels */}
            {Array.from({ length: TOTAL_SLOTS }).map((_, i) => {
              const hour = START_HOUR + Math.floor(i / SLOTS_PER_HOUR);
              const isHourStart = i % SLOTS_PER_HOUR === 0;
              return (
                <div
                  key={`label-${i}`}
                  style={{
                    gridRow: i + 2,
                    gridColumn: 1,
                    textAlign: "right",
                    paddingRight: "10px",
                    borderBottom: isHourStart ? "none" : "1px solid #f1f3f4",
                    borderRight: "1px solid #ddd",
                    background: "#fff",
                    position: "sticky",
                    left: 0,
                    zIndex: 30,
                  }}
                >
                  {isHourStart && (
                    <span
                      style={{
                        display: "block",
                        transform: "translateY(-50%)",
                        fontSize: "0.75rem",
                        color: "#70757a",
                      }}
                    >
                      {hour}:00
                    </span>
                  )}
                </div>
              );
            })}

            {/* Grid Cells */}
            {days.map((day, dayIndex) => {
              const isToday = day.isSame(moment(), "day");
              return Array.from({ length: TOTAL_SLOTS }).map((_, slotIndex) => {
                const isHourStart = slotIndex % SLOTS_PER_HOUR === 0;
                return (
                  <div
                    key={`${dayIndex}-${slotIndex}`}
                    onMouseDown={() => handleMouseDown(dayIndex, slotIndex)}
                    onMouseEnter={() => handleMouseEnter(dayIndex, slotIndex)}
                    style={{
                      gridRow: slotIndex + 2,
                      gridColumn: dayIndex + 2,
                      borderLeft: "1px solid #ddd",
                      borderBottom: isHourStart
                        ? "1px dotted #e0e0e0"
                        : "1px solid #e0e0e0",
                      cursor: "pointer",
                      backgroundColor: isToday
                        ? "rgba(232, 240, 254, 0.3)"
                        : "white",
                    }}
                  />
                );
              });
            })}

            {eventBlocks}
            {selectionBlock}
          </div>
        </div>

        {/* Edit/Delete Modal */}
        <ModalContainer
          open={showEditModal}
          title="Editar Disponibilidad"
          closeFn={() => setShowEditModal(false)}
          size="sm"
        >
          <div className="p-2">
            <div className="mb-3">
              <label className="form-label">Titulo</label>
              <input
                type="text"
                className="form-control"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
              />
            </div>
            <div className="row mb-4">
              <div className="col">
                <label className="form-label">Inicio</label>
                <input
                  type="time"
                  className="form-control"
                  value={editStartTime}
                  onChange={(e) => setEditStartTime(e.target.value)}
                />
              </div>
              <div className="col">
                <label className="form-label">Fin</label>
                <input
                  type="time"
                  className="form-control"
                  value={editEndTime}
                  onChange={(e) => setEditEndTime(e.target.value)}
                />
              </div>
            </div>

            <div className="d-flex justify-content-between">
              <button
                className="btn btn-danger"
                onClick={handleDelete}
                title="Eliminar"
              >
                <i className="fa fa-trash"></i> Eliminar
              </button>
              <button className="btn btn-primary" onClick={handleUpdate}>
                Guardar Cambios
              </button>
            </div>
          </div>
        </ModalContainer>

        {/* Interview Details Modal */}
        <ModalContainer
          open={showInterviewModal}
          title="Detalle de Entrevista"
          closeFn={() => setShowInterviewModal(false)}
          size="sm"
        >
          {selectedEvent && selectedEvent.application ? (
            <div className="p-3">
              <h5>
                Candidato: {selectedEvent.application.person?.firstName}{" "}
                {selectedEvent.application.person?.lastName}
              </h5>
              <p>
                <strong>Hora:</strong>{" "}
                {moment(selectedEvent.startTime).format("HH:mm")} -{" "}
                {moment(selectedEvent.endTime).format("HH:mm")}
              </p>
              <p>
                <strong>Puesto:</strong>{" "}
                {selectedEvent.application?.vacancy?.position?.name || "N/A"}
              </p>
              <hr />
              <div className="d-grid">
                <button
                  className="btn btn-primary"
                  onClick={() => goToCandidateForm(selectedEvent)}
                >
                  <i className="fa fa-eye me-2"></i> Ver Candidato Completo
                </button>
              </div>
            </div>
          ) : (
            <></>
          )}
        </ModalContainer>

        {/* My Interviews Today Modal */}
        <ModalContainer
          open={showMyInterviewsModal}
          title={`Entrevistas del ${moment().format("DD/MM/YYYY")}`}
          closeFn={() => setShowMyInterviewsModal(false)}
        >
          <div className="p-3">
            {myInterviewsToday.length === 0 ? (
              <div className="text-center text-muted">
                No tienes entrevistas asignadas para este día.
              </div>
            ) : (
              <div className="list-group">
                {myInterviewsToday.map((int) => (
                  <div
                    key={int.id}
                    className="list-group-item d-flex justify-content-between align-items-center"
                  >
                    <div>
                      <h6 className="mb-1">
                        {int.application.person?.firstName}{" "}
                        {int.application.person?.lastName}
                      </h6>
                      <small className="text-muted">
                        <i className="fa fa-clock me-1"></i>
                        {moment(int.startTime).format("HH:mm")} -{" "}
                        {moment(int.endTime).format("HH:mm")}
                      </small>
                    </div>
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => goToCandidateForm(int)}
                    >
                      Ver Ficha
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </ModalContainer>
      </div>
    </Layout>
  );
};

export default LeaderCalendar;
